export const ALLOWED_GP_EXTENSIONS = [".gp5", ".gp4", ".gp3", ".gpx", ".gp"];
export const MAX_GP_FILE_BYTES = 15 * 1024 * 1024;

export function hasAllowedGpExtension(name: string): boolean {
  const lower = name.toLowerCase();
  return ALLOWED_GP_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

/** Returns a Persian error message, or null when the file name/size are acceptable. */
export function validateGpMeta(file: {
  name: string;
  size: number;
}): string | null {
  if (!hasAllowedGpExtension(file.name)) {
    return "فرمت فایل پشتیبانی نمی‌شود. فقط فایل‌های Guitar Pro (‎.gp5, .gp4, .gp3, .gpx, .gp‎) مجاز هستند.";
  }
  if (file.size > MAX_GP_FILE_BYTES) {
    return "حجم فایل بیشتر از ۱۵ مگابایت است.";
  }
  if (file.size === 0) {
    return "فایل خالی است.";
  }
  return null;
}

const INVALID_CONTENT =
  "محتوای فایل معتبر نیست یا فایل Guitar Pro آسیب دیده است.";

export type GpKind = "gp3-5" | "gpx" | "gp7";

function includesAscii(bytes: Uint8Array, needleStr: string): boolean {
  if (bytes.length < needleStr.length) return false;
  const firstByte = needleStr.charCodeAt(0);
  const needleLen = needleStr.length;
  let idx = 0;
  while ((idx = bytes.indexOf(firstByte, idx)) !== -1) {
    if (idx + needleLen > bytes.length) return false;
    let match = true;
    for (let j = 1; j < needleLen; j++) {
      if (bytes[idx + j] !== needleStr.charCodeAt(j)) {
        match = false;
        break;
      }
    }
    if (match) return true;
    idx++;
  }
  return false;
}

/**
 * Kind implied by the first bytes, or null.
 * Extension-independent on purpose (real files are often misnamed).
 */
export function detectGpKind(buffer: Uint8Array | ArrayBuffer): GpKind | null {
  const buf = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  // GP3/GP4/GP5: byte 0 = length of the version string (24 for "FICHIER GUITAR PRO v5.10"), then the string
  // itself, padded to 30 bytes in full files. Minimum length 25 bytes (1 + 24).
  if (buf.length >= 25 && buf[0] >= 20 && buf[0] <= 30) {
    const head = new TextDecoder("latin1").decode(buf.subarray(1, 20));
    if (head === "FICHIER GUITAR PRO ") return "gp3-5";
  }
  // GPX (Guitar Pro 6): BCFZ (compressed) or BCFS container header.
  if (buf.length >= 8) {
    const head = new TextDecoder("latin1").decode(buf.subarray(0, 4));
    if (head === "BCFZ" || head === "BCFS") return "gpx";
  }
  // .gp (Guitar Pro 7/8): ZIP local file header PK\x03\x04, and the archive must hold Content/score.gpif
  // (stored uncompressed in the ZIP directory, so a byte search is enough; alphaTab needs that entry).
  if (
    buf.length >= 30 &&
    buf[0] === 0x50 &&
    buf[1] === 0x4b &&
    buf[2] === 0x03 &&
    buf[3] === 0x04 &&
    includesAscii(buf, "Content/score.gpif")
  ) {
    return "gp7";
  }
  return null;
}

export interface InspectGpResult {
  error: string | null;
  tracks: Array<{ trackIndex: number; name: string }> | null;
}

/** Inspects Guitar Pro file content, validates signature and extracts tracks via alphaTab. */
export async function inspectGpFile(
  _name: string,
  buffer: ArrayBuffer | Uint8Array,
): Promise<InspectGpResult> {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);

  if (bytes.length === 0) {
    return { error: "فایل خالی است.", tracks: null };
  }

  const kind = detectGpKind(bytes);
  if (!kind) {
    return { error: INVALID_CONTENT, tracks: null };
  }

  // FRE-142: the alphaTab chunk may be unavailable offline (never loaded before). That is not a bad file:
  // the magic-header check above already passed, so accept it. A parse failure below is still invalid.
  let alphaTab: typeof import("@coderline/alphatab");
  try {
    alphaTab = await import("@coderline/alphatab");
  } catch {
    return { error: null, tracks: null };
  }

  try {
    const score = alphaTab.importer.ScoreLoader.loadScoreFromBytes(
      bytes,
      new alphaTab.Settings(),
    );
    if (!score || !score.tracks || score.tracks.length === 0) {
      return { error: INVALID_CONTENT, tracks: null };
    }
    const tracks = score.tracks.slice(0, 64).map((t, i) => ({
      trackIndex: i,
      name: (t.name?.trim() || `Track ${i + 1}`).slice(0, 200),
    }));
    return { error: null, tracks };
  } catch {
    return { error: INVALID_CONTENT, tracks: null };
  }
}

/** Cheap content check: validates signature and alphaTab parse, returning error message or null. */
export async function validateGpContent(
  name: string,
  buffer: ArrayBuffer | Uint8Array,
): Promise<string | null> {
  const res = await inspectGpFile(name, buffer);
  return res.error;
}

/** File extension implied by the magic bytes (used as the upload file name suffix), or null when unknown. */
export function inferGpExtension(buffer: ArrayBuffer | Uint8Array): string | null {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  if (bytes.length >= 25 && bytes[0] >= 20 && bytes[0] <= 30) {
    const head = new TextDecoder("latin1").decode(bytes.subarray(1, Math.min(bytes.length, 30)));
    const m = /^FICHIER GUITAR PRO v([345])/.exec(head);
    if (m) return `.gp${m[1]}`;
    if (head.startsWith("FICHIER GUITAR PRO")) return ".gp5";
  }
  if (bytes.length >= 8) {
    const head = new TextDecoder("latin1").decode(bytes.subarray(0, 4));
    if (head === "BCFZ" || head === "BCFS") return ".gpx";
  }
  if (
    bytes.length >= 30 &&
    bytes[0] === 0x50 &&
    bytes[1] === 0x4b &&
    bytes[2] === 0x03 &&
    bytes[3] === 0x04 &&
    includesAscii(bytes, "Content/score.gpif")
  ) {
    return ".gp";
  }
  return null;
}

/** SHA-256 hex of the buffer; falls back to size+checksum where crypto.subtle is unavailable. */
export async function hashBuffer(buffer: ArrayBuffer): Promise<string> {
  if (typeof crypto !== "undefined" && crypto.subtle) {
    const digest = await crypto.subtle.digest("SHA-256", buffer);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }
  const bytes = new Uint8Array(buffer);
  let h = 5381;
  for (let i = 0; i < bytes.length; i++) h = ((h << 5) + h + bytes[i]) | 0;
  return `fallback-${bytes.length}-${h}`;
}
